import Currency from '@v2/shared/components/Currency';
import { useSite } from '@v2/shared/lib/site';
import { PRODUCT_TYPE_BOOK, PRODUCT_TYPE_MAGAZINE, SaleProduct } from '@v2/shared/lib/megaSale';

type Props = {
  product: SaleProduct;
  /** The price actually being charged, for the term selected. */
  price: number;
  /** The term's percentage adjustment, which scales the struck-through figure too. */
  pricePercentage: number;
  showCoverPrice: boolean;
};

/**
 * The struck-through comparison price.
 *
 * `shared/components/Msrp` is the site's version and takes a `Sku`, which a sale product
 * is not — the JSON carries a flat `msrp`/`original_price` pair rather than a sku
 * catalogue. It also has to answer a question the site's does not: what the figure is
 * *called*, which on this page is one of three things depending on what is being sold.
 *
 * Suppressed rather than shown as equal when the comparison is not above the price. A
 * struck-through number the same as the one beside it reads as a bug, and a lower one
 * reads as a lie.
 */
export default function SaleMsrp({ product, price, pricePercentage, showCoverPrice }: Props) {
  const site = useSite();

  // Magazines and books only. Nothing else on a sale has a comparison price.
  if (product.product_type !== PRODUCT_TYPE_MAGAZINE && product.product_type !== PRODUCT_TYPE_BOOK) {
    return null;
  }

  // A single issue has no cover price to beat — it *is* the cover price.
  if (product.product_type === PRODUCT_TYPE_MAGAZINE && product.attributes.issues === 1) {
    return null;
  }

  const label = () => {
    if (product.product_type !== PRODUCT_TYPE_MAGAZINE) {
      return 'Normally';
    }

    return product.is_bundle ? 'Purchased Separately' : 'Cover Price';
  };

  const scale = 1 + pricePercentage / 100;

  if (product.original_price > 0 && !showCoverPrice) {
    return (
      <span className="text-sm font-medium text-slate-500">
        {label()}:{' '}
        <span className="line-through">
          <Currency value={product.original_price * scale} currencyCode={site.currency} />
        </span>
      </span>
    );
  }

  if (product.msrp > 0) {
    const msrp = Math.round((product.msrp * scale + Number.EPSILON) * 100) / 100;

    if (msrp > price) {
      return (
        <span className="text-sm font-medium text-slate-500">
          {label()}:{' '}
          <span className="line-through">
            <Currency value={msrp} currencyCode={site.currency} />
          </span>
        </span>
      );
    }
  }

  return null;
}
